'use client'

import type { HTMLAttributeAnchorTarget } from 'react'
import Link from 'next/link'
import { motion } from 'motion/react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import { poppins } from '~/font'
import { useIsActive } from './use-is-active'

interface AnimatedDesktopButtonProps {
  className?: string
  tabIndex?: number
  whileHoverScale?: number
  whileTapScale?: number
  children: React.ReactNode
  disabled?: boolean
}

function AnimatedDesktopButton({
  className,
  tabIndex,
  whileHoverScale = 1,
  whileTapScale = 1,
  children,
  disabled,
}: AnimatedDesktopButtonProps) {
  return (
    <motion.button
      className={cn(poppins.className, className)}
      type='button'
      tabIndex={tabIndex}
      disabled={disabled}
      whileHover={disabled ? undefined : { scale: whileHoverScale }}
      whileTap={disabled ? undefined : { scale: whileTapScale }}
      aria-disabled={disabled}
    >
      {children}
    </motion.button>
  )
}

interface NavDesktopButtonProps {
  className?: string
  href: string
  target?: HTMLAttributeAnchorTarget
  text: string
}

export function NavDesktopButton({ className, href, target, text }: NavDesktopButtonProps) {
  const isActive = useIsActive(href, target)

  const buttonContent = (
    <AnimatedDesktopButton
      tabIndex={isActive ? undefined : -1}
      whileHoverScale={isActive ? undefined : 1.05}
      whileTapScale={isActive ? undefined : 0.95}
      disabled={isActive}
      className={cn(
        typographyVariants({ variant: 'label-md' }),
        'text-neutral-black rounded-full px-4 py-2',
        isActive ? 'bg-blue-100/20' : 'bg-transparent',
        className,
      )}
    >
      {text}
    </AnimatedDesktopButton>
  )

  if (isActive) {
    return <span aria-current='page'>{buttonContent}</span>
  }

  return (
    <Link href={href} className='text-neutrals-n-75 h-fit w-fit' target={target} prefetch={target !== '_blank'}>
      {buttonContent}
    </Link>
  )
}
